import { formattedDollarsToCents } from '@fruitful/fruitils';
import {
    BillActivationStatus,
    type BillCategory,
    BillCreationType,
    BillingCadence,
} from '@fruitful/types';
import React, { useEffect, useRef, useState } from 'react';
import {
    DollarInput,
    Input,
    Spinner,
    SpinnerSize,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { useUpsertBillAndRefetch } from '../../bills/useUpsertBill';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { captureException } from '../../logger/Logger';
import { moneyMapperBillCadencePickerOptions } from '../utils/moneyMapperBillCadencePickerOptions';
import { GroveMoneyMapPickerField } from './GroveMoneyMapPickerField';
import {
    isFocusMovingWithinMoneyMapTableRow,
    type MoneyMapTableRowBlurEvent,
} from './moneyMapperTableRowFocus';
import {
    INCOME_TABLE_ACTIONS_OUTER_WIDTH,
    INCOME_TABLE_ROW_PADDING_HORIZONTAL,
    incomeTableAmountColumnProps,
    incomeTableBillCategoryColumnProps,
    incomeTablePayCycleColumnProps,
    incomeTableRowFocusActive,
    incomeTableSourceColumnProps,
} from './take-home-pay/moneyMapperIncomeTableLayout';

/** Sent on Money Mapper create; server resolves the real linked account. */
const MONEY_MAPPER_ACCOUNT_ID = 'MONEY_MAPPER_ACCOUNT_ID';

export interface MoneyMapperNewBillRowProps {
    membershipId: string;
    /**
     * Default / fixed category. When `categoryPickerOptions` is set (All tab),
     * this is the initial picker value; saves use the picker's selection.
     */
    category: BillCategory;
    /** When set, show a category column (same layout as All bills table). */
    categoryPickerOptions?: Array<{ value: BillCategory; label: string }>;
    disabled: boolean;
    onSaved: () => void;
}

interface NewBillDraft {
    displayName: string;
    payee: string;
    amountStr: string;
    cadence: BillingCadence;
}

const emptyDraft = (): NewBillDraft => ({
    displayName: '',
    payee: '',
    amountStr: '$ ',
    cadence: BillingCadence.MONTHLY,
});

export const MoneyMapperNewBillRow = ({
    membershipId,
    category,
    categoryPickerOptions,
    disabled,
    onSaved,
}: MoneyMapperNewBillRowProps) => {
    const toast = useToastController();
    const { upsertBillAsync } = useUpsertBillAndRefetch(membershipId);

    const showCategoryPicker =
        categoryPickerOptions != null && categoryPickerOptions.length > 0;

    const [draft, setDraft] = useState<NewBillDraft>(() => emptyDraft());
    const [pickerCategory, setPickerCategory] =
        useState<BillCategory>(category);
    const [isSaving, setIsSaving] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const pickerCategoryRef = useRef(pickerCategory);
    pickerCategoryRef.current = pickerCategory;
    const tableRowRef = useRef<HTMLElement | null>(null);
    const isMountedRef = useRef(true);
    // Ref-based guard so picker onChange → tryCreate + the subsequent
    // onTriggerBlur → persistIfLeavingRow → tryCreate sequence can't fire
    // two saves before the first setIsSaving(true) causes a re-render.
    const isSavingRef = useRef(false);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const rowDisabled = disabled || isSaving;

    const tryCreate = async (
        next: NewBillDraft,
        billCategoryOverride?: BillCategory,
    ) => {
        if (rowDisabled) {
            return;
        }

        const name = next.displayName.trim();
        if (name === '') {
            return;
        }

        const amountCents = formattedDollarsToCents(next.amountStr);
        if (!Number.isFinite(amountCents) || amountCents <= 0) {
            return;
        }

        const billCategory =
            billCategoryOverride ??
            (showCategoryPicker ? pickerCategoryRef.current : category);

        isSavingRef.current = true;
        setIsSaving(true);
        try {
            const payeeTrimmed = next.payee.trim();
            await upsertBillAsync({
                membershipId,
                displayName: name,
                payee: payeeTrimmed !== '' ? payeeTrimmed : name,
                amount: amountCents,
                accountId: MONEY_MAPPER_ACCOUNT_ID,
                category: billCategory,
                cadence: next.cadence,
                activationStatus: BillActivationStatus.ACTIVE,
                creationType: BillCreationType.MONEY_MAPPER,
            });
            toast.show('Bill saved', MutationSuccessToastOptions);
            onSaved();
        } catch (error) {
            captureException(error);
            toast.show('Error saving bill', MutationErrorToastOptions(error));
        } finally {
            isSavingRef.current = false;
            if (isMountedRef.current) {
                setIsSaving(false);
            }
        }
    };

    const persistIfLeavingRow = (event: MoneyMapTableRowBlurEvent) => {
        const root = tableRowRef.current as unknown as
            | Element
            | null
            | undefined;
        if (isFocusMovingWithinMoneyMapTableRow(root, event)) {
            return;
        }
        void tryCreate(draftRef.current);
    };

    return (
        <YStack width="100%">
            <XStack
                ref={tableRowRef}
                width="100%"
                alignItems="flex-start"
                paddingVertical={12}
                paddingHorizontal={INCOME_TABLE_ROW_PADDING_HORIZONTAL}
                gap={8}
                {...incomeTableRowFocusActive}
            >
                <YStack {...incomeTableSourceColumnProps}>
                    <Input
                        size="regular"
                        value={draft.displayName}
                        onChangeText={(t) => {
                            setDraft((d) => ({ ...d, displayName: t }));
                        }}
                        onBlur={(e) => {
                            if (
                                !isFocusMovingWithinMoneyMapTableRow(
                                    tableRowRef.current as unknown as Element | null,
                                    e,
                                )
                            ) {
                                void tryCreate(draftRef.current);
                            }
                        }}
                        placeholder="Bill name"
                        disabled={rowDisabled}
                    />
                </YStack>
                {showCategoryPicker ? (
                    <YStack {...incomeTableBillCategoryColumnProps}>
                        <GroveMoneyMapPickerField
                            size="regular"
                            value={pickerCategory}
                            options={categoryPickerOptions ?? []}
                            disabled={rowDisabled}
                            onTriggerBlur={persistIfLeavingRow}
                            onChange={(v) => {
                                pickerCategoryRef.current = v;
                                setPickerCategory(v);
                                void tryCreate(draftRef.current, v);
                            }}
                        />
                    </YStack>
                ) : null}
                <YStack {...incomeTableAmountColumnProps}>
                    <DollarInput
                        size="regular"
                        value={draft.amountStr}
                        onChangeText={(t) => {
                            setDraft((d) => ({ ...d, amountStr: t }));
                        }}
                        onBlur={(e) => {
                            if (
                                !isFocusMovingWithinMoneyMapTableRow(
                                    tableRowRef.current as unknown as Element | null,
                                    e,
                                )
                            ) {
                                void tryCreate(draftRef.current);
                            }
                        }}
                        allowDecimals
                        disabled={rowDisabled}
                    />
                </YStack>
                <YStack {...incomeTablePayCycleColumnProps}>
                    <GroveMoneyMapPickerField
                        size="regular"
                        value={draft.cadence}
                        options={moneyMapperBillCadencePickerOptions}
                        disabled={rowDisabled}
                        onTriggerBlur={persistIfLeavingRow}
                        onChange={(v) => {
                            const next = {
                                ...draftRef.current,
                                cadence: v,
                            };
                            draftRef.current = next;
                            setDraft(next);
                            void tryCreate(next);
                        }}
                    />
                </YStack>
                <YStack
                    width={INCOME_TABLE_ACTIONS_OUTER_WIDTH}
                    flexShrink={0}
                    minHeight={44}
                    alignItems="flex-end"
                    justifyContent="center"
                    paddingTop={6}
                >
                    {isSaving ? <Spinner size={SpinnerSize.SMALL} /> : null}
                </YStack>
            </XStack>
        </YStack>
    );
};
